import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../config/api';
import PasswordField from '../components/PasswordField';
import PasswordChangeDialog from '../components/PasswordChangeDialog';
import StatementDialog from '../components/StatementDialog';
import './login-features.css';

const loginFeatures = [
  ['Flight scheduling', 'Aircraft, instructors, rentals, and solo flights in one calendar.'],
  ['Scheduling assistant', 'Find a time for your next lesson or solo flight.'],
  ['Multiple locations', 'Coordinate aircraft and instructors across your airports.'],
  ['Training progress', 'Track requirements, maneuvers, and certification milestones.'],
  ['Instructor availability', 'Work schedules, location-specific rates, and availability overrides.'],
  ['Fleet management', 'Aircraft profiles, equipment, photos, and availability.'],
  ['Weather at a glance', 'METARs, TAFs, wind, and flight conditions.'],
  ['Mobile reservations', 'Book, update, and cancel flights from your phone.'],
  ['Safety pilot coordination', 'Invite a safety pilot, confirm requests, and share rental charges.'],
  ['Aircraft maintenance', 'Track maintenance records, squawks, and aircraft blockouts.'],
  ['Flight records', 'Aircraft logs, checkouts, check-ins, and flight log entries.'],
  ['Lesson records', 'Record flight instruction, ground training, and instructor feedback.'],
  ['Ground school', 'Organize classes, aviation events, and training sessions.'],
  ['Quizzes and learning', 'Practice knowledge and review instructor feedback.'],
  ['Training packages', 'Manage your organization\'s training offerings.'],
  ['Expense tracking', 'Manage expenses, receipts, and receipt extraction.'],
  ['Instructor earnings', 'Track work lessons, expenses, and earnings statements.'],
  ['Aircraft owner statements', 'Manage owner expenses and aircraft statements.'],
  ['Announcements', 'Keep your organization informed with shared updates.'],
  ['Agreements and forms', 'Manage documents, agreements, and signatures.'],
  ['Website widgets', 'Share your fleet, instructors, and location-based prices.'],
  ['Website chatbot', 'Connect website visitors with your organization.'],
  ['Organization management', 'Manage people, roles, locations, and reservation types.'],
  ['Help shape Real Air Scheduling', 'Have an idea for a better training experience? Send feature requests directly to the Real Air Scheduling and help shape the tools you use.']
];

const Login = () => {
  const [featuresPaused, setFeaturesPaused] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [staySignedIn, setStaySignedIn] = useState(false);
  const [error, setError] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [showOrgSelection, setShowOrgSelection] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [loginResponse, setLoginResponse] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showLegalStatement, setShowLegalStatement] = useState(false);
  const [showPrivacyStatement, setShowPrivacyStatement] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [unsignedAgreements, setUnsignedAgreements] = useState([]);
  const [currentAgreement, setCurrentAgreement] = useState(null);
  const [agreementPdfUrl, setAgreementPdfUrl] = useState('');
  const [agreementSignData, setAgreementSignData] = useState({ signedName: '', dob: '', signedParent: '' });
  const [agreementError, setAgreementError] = useState('');
  const [signingAgreement, setSigningAgreement] = useState(false);
  const [agreementRead, setAgreementRead] = useState(false);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(false);
  const [userType, setUserType] = useState('');
  const [showOrgSearch, setShowOrgSearch] = useState(false);
  const [orgSearchName, setOrgSearchName] = useState('');
  const [orgSearchBase, setOrgSearchBase] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [registerData, setRegisterData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    dob: '',
    entityType: 'Independent Flight Instructor',
    businessName: '',
    businessDescription: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    businessPhone: '',
    businessEmail: '',
    homeAirport: ''
  });
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');

  const searchOrganizations = async () => {
    if (!orgSearchName && !orgSearchBase) {
      setError('Please enter either organization name or airport code');
      return;
    }
    
    try {
      const response = await apiClient('/organization/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: orgSearchName,
          base: orgSearchBase
        })
      });
      
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
        if (results.length === 0) {
          setError('No organizations found matching your search criteria');
        }
      } else {
        setError('Failed to search organizations');
      }
    } catch (error) {
      setError(`Search error: ${error.message}`);
    }
  };

  const handleUserTypeSelection = (type) => {
    setUserType(type);
    setShowUserTypeSelection(false);
    
    if (type === 'organization') {
      setIsRegister(true);
    } else {
      setShowOrgSearch(true);
    }
  };

  const handleOrgSearchContinue = () => {
    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }
    setShowOrgSearch(false);
    setIsRegister(true);
  };
  const [successMessage, setSuccessMessage] = useState('');
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useApp();

  React.useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiClient('/role/list');
        if (response.ok) {
          const roleList = await response.json();
          setRoles(roleList);
        }
      } catch (error) {
        // Ignore 401 errors on login page - user not authenticated yet
      }
    };
    fetchRoles();
  }, []);

  const validatePassword = (password) => {
    const minLength = 10;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < minLength) {
      return 'Password must be at least 10 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (isRegister) {
      // Registration logic
      if (registerData.password !== registerData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      // Password validation
      const passwordError = validatePassword(registerData.password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(registerData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      
      // Phone validation (US format)
      const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
      const phoneToValidate = userType === 'organization' ? registerData.businessPhone : registerData.phone;
      if (!phoneRegex.test(phoneToValidate)) {
        setError('Please enter a valid phone number (e.g., 555-555-5555)');
        return;
      }
      
      try {
        let endpoint = '/user/register-with-organization';
        let payload = {
          userData: {
            username: registerData.username,
            password: registerData.password,
            firstName: registerData.firstName,
            lastName: registerData.lastName,
            email: registerData.email
          }
        };
        
        if (userType === 'organization') {
          // Organization registration
          payload.organizationData = {
            name: registerData.businessName,
            description: registerData.businessDescription,
            address: registerData.businessAddress,
            city: registerData.businessCity,
            state: registerData.businessState,
            zipCode: registerData.businessZip,
            phone: registerData.businessPhone,
            email: registerData.businessEmail,
            type: registerData.entityType,
            base: registerData.homeAirport
          };
        } else {
          // Student/renter registration
          endpoint = '/user/register';
          payload = {
            ...payload.userData,
            phone: registerData.phone,
            orgId: selectedOrg.id,
            role: 'Students',
            orgRole: 'client'
          };
        }
        
        const response = await apiClient(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        
        
        if (response.ok) {
          setSuccessMessage('You have successfully registered. Check your email to confirm your registration.');
          setRegisterData({
            username: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            dob: '',
            entityType: 'Independent Flight Instructor',
            businessName: '',
            businessDescription: '',
            businessAddress: '',
            businessCity: '',
            businessState: '',
            businessZip: '',
            businessPhone: '',
            businessEmail: '',
            homeAirport: ''
          });
          setDobMonth('');
          setDobDay('');
          setDobYear('');
          // Redirect to login screen after 5 seconds
          setTimeout(() => {
            setIsRegister(false);
            setSuccessMessage('');
          }, 5000);
        } else {
          const errorText = await response.text();
        
        }
      } catch (error) {
        setError(`Connection error: ${error.message}`);
      }
    } else {
      // Login logic
      try {
        const response = await apiClient('/user/retrieve', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: username,
            password: password
          })
        });
        if (response.ok) {
          const user = await response.json();
          
          
          // Check if user has multiple organizations
          if (user.organizations && user.organizations.length > 1) {
            setLoginResponse(user);
            setOrganizations(user.organizations);
            setShowOrgSelection(true);
            return;
          }
          
          // Single organization or no organizations - proceed with login
          await processLoginRequirements(user);
        } else {
          const errorText = await response.text();
          let serverMessage;
          try {
            const data = JSON.parse(errorText);
            if (typeof data?.error === 'string') serverMessage = data.error;
          } catch {
            // Proxies may return HTML rather than the API's JSON error.
          }
          if (response.status >= 500) {
            console.error('Login service failed:', response.status);
            setError(`Login service error (HTTP ${response.status}). Please try again or contact an administrator.`);
          } else if (response.status === 401) {
            setError(serverMessage || 'Invalid username or password');
          } else {
            setError(serverMessage || `Login failed (HTTP ${response.status}). Please try again.`);
          }
        }
      } catch (error) {
        
        setError(`Connection error: ${error.message}`);
      }
    }
  };

  const processLoginRequirements = async (user) => {
    setPendingUser(user);
    
    // Check if password change is required
    if (user.user?.isPasswordChangeRequired === 'Y') {
      setShowPasswordChange(true);
      return;
    }
    
    // Check if legal statement needs acknowledgment
    if (user.user?.isLegalStatementAcknowledged === 'N') {
      setShowLegalStatement(true);
      return;
    }
    
    // Check if privacy statement needs acknowledgment
    if (user.user?.isPrivacyStatementAcknowledged === 'N') {
      setShowPrivacyStatement(true);
      return;
    }
    
    // All requirements met, complete login
    await completeLogin(user);
  };

  const handlePasswordChangeComplete = () => {
    setShowPasswordChange(false);
    // After password change, check other requirements
    if (pendingUser.user?.isLegalStatementAcknowledged === 'N') {
      setShowLegalStatement(true);
    } else if (pendingUser.user?.isPrivacyStatementAcknowledged === 'N') {
      setShowPrivacyStatement(true);
    } else {
      completeLogin(pendingUser);
    }
  };

  const handleLegalStatementAcknowledge = () => {
    setShowLegalStatement(false);
    // Update pending user to reflect acknowledgment
    if (pendingUser) {
      pendingUser.user.isLegalStatementAcknowledged = 'Y';
    }
    // Check if privacy statement needs acknowledgment
    if (pendingUser.user?.isPrivacyStatementAcknowledged === 'N') {
      setShowPrivacyStatement(true);
    } else {
      completeLogin(pendingUser);
    }
  };

  const handlePrivacyStatementAcknowledge = () => {
    setShowPrivacyStatement(false);
    if (pendingUser) {
      pendingUser.user.isPrivacyStatementAcknowledged = 'Y';
    }
    completeLogin(pendingUser);
  };

  const loadAgreementPdf = async (agreementId, sessionId, userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/${agreementId}/pdf`, {
        headers: { 'session-id': sessionId }
      });
      if (response.ok) {
        const blob = await response.blob();
        setAgreementPdfUrl(URL.createObjectURL(blob));
        // Record that user has viewed the agreement
        fetch(`${API_BASE_URL}/org-agreement/viewed/${agreementId}/${userId}`, {
          method: 'POST',
          headers: { 'session-id': sessionId }
        }).catch(err => console.error('Error recording view:', err));
      }
    } catch (error) {
      console.error('Error loading agreement PDF:', error);
    }
  };

  const handleSignAgreement = async () => {
    if (!agreementSignData.signedName.trim()) {
      setAgreementError('Please enter your full name to sign');
      return;
    }
    if (!agreementSignData.dob) {
      setAgreementError('Please enter your date of birth');
      return;
    }
    const isMinor = (() => {
      const dob = new Date(agreementSignData.dob);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
      return age < 18;
    })();
    if (isMinor && !agreementSignData.signedParent.trim()) {
      setAgreementError('Parent/Guardian signature is required for users under 18');
      return;
    }
    setSigningAgreement(true);
    setAgreementError('');
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/signed/${currentAgreement.id}/${pendingUser.user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': pendingUser.sessionId },
        body: JSON.stringify({
          signedName: agreementSignData.signedName,
          dob: agreementSignData.dob,
          signedParent: agreementSignData.signedParent || null
        })
      });
      if (response.ok) {
        if (agreementPdfUrl) URL.revokeObjectURL(agreementPdfUrl);
        setAgreementPdfUrl('');
        setAgreementSignData({ signedName: '', dob: '', signedParent: '' });
        const remaining = unsignedAgreements.filter(a => a.id !== currentAgreement.id);
        setUnsignedAgreements(remaining);
        if (remaining.length > 0) {
          setCurrentAgreement(remaining[0]);
          setAgreementRead(false);
          loadAgreementPdf(remaining[0].id, pendingUser.sessionId, pendingUser.user.id);
        } else {
          setCurrentAgreement(null);
          completeLogin(pendingUser);
        }
      } else {
        const err = await response.text();
        setAgreementError(`Error: ${err}`);
      }
    } catch (error) {
      setAgreementError(`Error: ${error.message}`);
    } finally {
      setSigningAgreement(false);
    }
  };

  const handleDialogCancel = () => {
    setShowPasswordChange(false);
    setShowLegalStatement(false);
    setShowPrivacyStatement(false);
    setPendingUser(null);
    setError('Login cancelled');
  };

  const completeLogin = async (user) => {
    // Check for unsigned agreements first
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/unsigned`, {
        headers: { 'session-id': user.sessionId }
      });
      if (response.ok) {
        const unsigned = await response.json();
        if (Array.isArray(unsigned) && unsigned.length > 0) {
          setPendingUser(user);
          setUnsignedAgreements(unsigned);
          setCurrentAgreement(unsigned[0]);
          setAgreementRead(false);
          setAgreementSignData({ signedName: '', dob: '', signedParent: '' });
          loadAgreementPdf(unsigned[0].id, user.sessionId, user.user.id);
          return;
        }
      }
    } catch (error) {
      // proceed with login if check fails
    }
    // Map roleId to role name using pre-loaded roles or fetch if needed
    let roleName = null;

    if (user.user?.roleId) {
      let rolesToUse = roles;
      
      // If roles not loaded, fetch them now
      if (roles.length === 0) {
        try {
          const roleResponse = await apiClient('/role/list', {
            headers: {
              'session-id': user.sessionId
            }
          });
          if (roleResponse.ok) {
            rolesToUse = await roleResponse.json();
            setRoles(rolesToUse);
          }
        } catch (error) {
        }
      }
      
      if (rolesToUse.length > 0) {
        const matchingRole = rolesToUse.find(role => role.id == user.user.roleId);
        roleName = matchingRole?.name;
      }
    }
    
    // Ensure user object has id property, sessionId, email, and phone
    const userWithId = {
      ...user,
      id: user.id || user.userId || user.user_id,
      sessionId: user.sessionId,
      email: user.email,
      phone: user.phone
    };
    
    // Set role from role name if available
    if (roleName && userWithId.user) {
      userWithId.user.role = roleName;
    }
    
    // Remove password and username from user object for security
    if (userWithId.user && userWithId.user.password) {
      delete userWithId.user.password;
    }
    if (userWithId.user && userWithId.user.username) {
      delete userWithId.user.username;
    }
    if (userWithId.password) {
      delete userWithId.password;
    }
    if (userWithId.username) {
      delete userWithId.username;
    }

    setCurrentUser(userWithId, { staySignedIn });
    // Redirect limited users to events view
    if (userWithId.user?.status === 'limited') {
      navigate('/events');
    } else {
      navigate('/calendar');
    }

  };

  const handleOrgSelection = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization');
      return;
    }

    try {
      const response = await apiClient(`/organization/select/${selectedOrgId}`, {
        method: 'POST',
        headers: {
          'session-id': loginResponse.sessionId
        }
      });

      if (response.ok) {
        setShowOrgSelection(false);
        await processLoginRequirements(loginResponse);
      } else {
        const errorText = await response.text();
        setError(`Failed to select organization: ${errorText}`);
      }
    } catch (error) {
      setError(`Error selecting organization: ${error.message}`);
    }
  };

  if (currentUser?.sessionId) return <Navigate to={currentUser.user?.status === 'limited' ? '/events' : '/calendar'} replace />;

  return (
    <div className="login-page" style={{
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundImage: 'url(https://storage.googleapis.com/realair-images/sky_sunset.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      <div className="login-features" aria-hidden="true" data-paused={featuresPaused}>
        {[0, 1, 2].map(lane => <div className="login-features-lane" key={lane}>
          <div className="login-features-track">
            {[0, 1].map(copy => <ul className="login-features-list" key={copy}>
              {[...loginFeatures.slice(lane * 8), ...loginFeatures.slice(0, lane * 8)].map(([name, description]) => <li key={name}>
                <strong>{name}</strong>
                <span>{description}</span>
              </li>)}
            </ul>)}
          </div>
        </div>)}
      </div>
      <button type="button" className="login-features-toggle" aria-pressed={featuresPaused} onClick={() => setFeaturesPaused(previous => !previous)}>{featuresPaused ? 'Resume background' : 'Pause background'}</button>
      <div className="login-panel" style={{
        backgroundColor: 'white',
        padding: window.innerWidth <= 768 ? '1.5rem' : '3rem',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        width: window.innerWidth <= 768 ? '95vw' : '480px',
        maxWidth: '95vw'
      }}>
        <h1 style={{ 
          textAlign: 'center', 
          marginBottom: '2rem', 
          color: '#333',
          fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem',
          lineHeight: '1.2'
        }}>
          {isRegister ? (
            <>
              <span style={{ fontSize: '0.8em' }}>Register For</span><br/>
              <span style={{ color: '#667eea', fontWeight: 'bold' }}>Real Air Scheduling</span>
            </>
          ) : (
            <span style={{ color: '#667eea', fontWeight: 'bold' }}>Real Air Scheduling</span>
          )}
        </h1>
        
        <form onSubmit={handleSubmit}>
          {!isRegister ? (
            // Login Form
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Username:
                </label>
                <input 
                  type="text" 
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <PasswordField
                  label="Password:"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={staySignedIn} onChange={event => setStaySignedIn(event.target.checked)} />
                Stay signed in
              </label>
            </>
          ) : (
            // Registration Form - only show if userType is 'organization'
            userType === 'organization' ? (
            <div style={{ maxHeight: window.innerWidth <= 768 ? '400px' : '300px', overflowY: 'scroll', scrollbarWidth: 'auto', marginBottom: '1rem', padding: window.innerWidth <= 768 ? '10px' : '15px', backgroundColor: '#e6f3ff', borderRadius: '8px', border: '1px solid #cce7ff' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#007bff' }}>Personal Information</h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Username *</label>
                <input 
                  type="text" 
                  value={registerData.username}
                  onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Password *"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  required
                />
                <div style={{ fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                  Password must contain:
                  <ul style={{ margin: '0.25rem 0', paddingLeft: window.innerWidth <= 768 ? '0.75rem' : '1rem' }}>
                    <li>At least 10 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                    <li>One special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                  </ul>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Confirm Password *"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                  required
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name *</label>
                <input 
                  type="text" 
                  value={registerData.firstName}
                  onChange={(e) => setRegisterData({...registerData, firstName: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name *</label>
                <input 
                  type="text" 
                  value={registerData.lastName}
                  onChange={(e) => setRegisterData({...registerData, lastName: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Date of Birth *</label>
                <input 
                  type="date" 
                  value={registerData.dob}
                  onChange={(e) => setRegisterData({...registerData, dob: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email *</label>
                <input 
                  type="email" 
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <h3 style={{ margin: '1.5rem 0 1rem 0', color: '#007bff' }}>Flight Training Organization Information</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                If you are an independent flight instructor, enter your full name for business name.
              </p>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Entity Type *</label>
                <select 
                  value={registerData.entityType}
                  onChange={(e) => setRegisterData({...registerData, entityType: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                >
                  <option value="Independent Flight Instructor">Independent Flight Instructor</option>
                  <option value="Flying Club">Flying Club</option>
                  <option value="Flight School">Flight School</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {registerData.entityType} Name {registerData.entityType === 'Independent Flight Instructor' ? '(Doing business as)' : ''} *
                </label>
                <input 
                  type="text" 
                  value={registerData.businessName}
                  onChange={(e) => setRegisterData({...registerData, businessName: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Home Airport *</label>
                <input 
                  type="text" 
                  value={registerData.homeAirport}
                  onChange={(e) => setRegisterData({...registerData, homeAirport: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  placeholder="e.g., KBOS, KJFK"
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} Description</label>
                <textarea 
                  value={registerData.businessDescription}
                  onChange={(e) => setRegisterData({...registerData, businessDescription: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem', minHeight: '60px' }}
                  placeholder={`Brief description of your ${registerData.entityType.toLowerCase()}`}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} Address *</label>
                <input 
                  type="text" 
                  value={registerData.businessAddress}
                  onChange={(e) => setRegisterData({...registerData, businessAddress: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} City *</label>
                <input 
                  type="text" 
                  value={registerData.businessCity}
                  onChange={(e) => setRegisterData({...registerData, businessCity: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} State *</label>
                <input 
                  type="text" 
                  value={registerData.businessState}
                  onChange={(e) => setRegisterData({...registerData, businessState: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} ZIP *</label>
                <input 
                  type="text" 
                  value={registerData.businessZip}
                  onChange={(e) => setRegisterData({...registerData, businessZip: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} Phone *</label>
                <input 
                  type="tel" 
                  value={registerData.businessPhone}
                  onChange={(e) => setRegisterData({...registerData, businessPhone: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  placeholder="555-555-5555"
                  required 
                />
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                  Format: 555-555-5555 or (555) 555-5555
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>{registerData.entityType} Email *</label>
                <input 
                  type="email" 
                  value={registerData.businessEmail}
                  onChange={(e) => setRegisterData({...registerData, businessEmail: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
            </div>
            ) : userType === 'student' ? (
            // Student/Renter Registration Form
            <div style={{ maxHeight: window.innerWidth <= 768 ? '400px' : '300px', overflowY: 'scroll', scrollbarWidth: 'auto', marginBottom: '1rem', padding: window.innerWidth <= 768 ? '10px' : '15px', backgroundColor: '#e6f3ff', borderRadius: '8px', border: '1px solid #cce7ff' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#007bff' }}>Personal Information</h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Username *</label>
                <input 
                  type="text" 
                  value={registerData.username}
                  onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Password *"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  required
                />
                <div style={{ fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                  Password must contain:
                  <ul style={{ margin: '0.25rem 0', paddingLeft: window.innerWidth <= 768 ? '0.75rem' : '1rem' }}>
                    <li>At least 10 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                    <li>One special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                  </ul>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Confirm Password *"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                  required
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name *</label>
                <input 
                  type="text" 
                  value={registerData.firstName}
                  onChange={(e) => setRegisterData({...registerData, firstName: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name *</label>
                <input 
                  type="text" 
                  value={registerData.lastName}
                  onChange={(e) => setRegisterData({...registerData, lastName: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email *</label>
                <input 
                  type="email" 
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Phone *</label>
                <input 
                  type="tel" 
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
                  placeholder="555-555-5555"
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0f8ff', borderRadius: '6px', border: '1px solid #cce7ff' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#007bff' }}>Selected Flight School</h4>
                <p style={{ margin: '0', fontSize: '0.9rem' }}>
                  <strong>{selectedOrg?.name}</strong><br/>
                  {selectedOrg?.base} - {selectedOrg?.type}<br/>
                  {selectedOrg?.address}, {selectedOrg?.city}, {selectedOrg?.state}
                </p>
              </div>
            </div>
            ) : null
          )}
          
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          {successMessage && (
            <div style={{ color: 'green', marginBottom: '1rem', textAlign: 'center' }}>
              {successMessage}
            </div>
          )}
          
          <button 
            type="submit"
            style={{
              width: '100%',
              padding: window.innerWidth <= 768 ? '0.875rem' : '0.75rem',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: window.innerWidth <= 768 ? '16px' : '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '1rem',
              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
            }}
          >
            {isRegister ? 'Register' : 'Login'}
          </button>
          
          <div style={{ textAlign: 'center' }}>
            {isRegister ? (
              <button 
                type="button"
                onClick={() => {
                  setIsRegister(false);
                  setUserType('');
                  setSelectedOrg(null);
                  setError('');
                  setSuccessMessage('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back to Login
              </button>
            ) : (
              <button 
                type="button"
                onClick={() => {
                  setShowUserTypeSelection(true);
                  setError('');
                  setSuccessMessage('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Register
              </button>
            )}
          </div>
        </form>
      </div>
      
      {/* User Type Selection Modal */}
      {showUserTypeSelection && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Select Registration Type</h3>
            <p style={{ margin: '0 0 1.5rem 0', textAlign: 'center' }}>Are you registering as:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button 
                onClick={() => handleUserTypeSelection('organization')}
                style={{ padding: '1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem' }}
              >
                Independent Flight Instructor / Flight School / Flying Club
              </button>
              <button 
                onClick={() => handleUserTypeSelection('student')}
                style={{ padding: '1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '1rem' }}
              >
                Student / Renter
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button 
                onClick={() => setShowUserTypeSelection(false)}
                style={{ background: 'none', border: 'none', color: '#667eea', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Organization Search Modal */}
      {showOrgSearch && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '500px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Find Your Flight School</h3>
            <p style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Enter the name and/or airport code of your flight school:</p>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Flight School Name:</label>
              <input 
                type="text" 
                value={orgSearchName}
                onChange={(e) => setOrgSearchName(e.target.value)}
                placeholder="e.g., Elite Flight Training"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Airport Code:</label>
              <input 
                type="text" 
                value={orgSearchBase}
                onChange={(e) => setOrgSearchBase(e.target.value)}
                placeholder="e.g., JFK, BOS"
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '1rem' }}
              />
            </div>
            
            <button 
              onClick={searchOrganizations}
              style={{ width: '100%', padding: '0.75rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginBottom: '1rem' }}
            >
              Search
            </button>
            
            {searchResults.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Select Your Flight School:</h4>
                {searchResults.map(org => (
                  <label key={org.id} style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <input 
                      type="radio" 
                      name="selectedOrg" 
                      value={org.id} 
                      checked={selectedOrg?.id === org.id}
                      onChange={() => setSelectedOrg(org)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <strong>{org.name}</strong><br/>
                    <small>{org.base} - {org.type}<br/>{org.address}, {org.city}, {org.state}</small>
                  </label>
                ))}
              </div>
            )}
            
            {error && (
              <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                {error}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              {selectedOrg && (
                <button 
                  onClick={handleOrgSearchContinue}
                  style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Continue
                </button>
              )}
              <button 
                onClick={() => {
                  setShowOrgSearch(false);
                  setSearchResults([]);
                  setSelectedOrg(null);
                  setOrgSearchName('');
                  setOrgSearchBase('');
                  setError('');
                }}
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Organization Selection Modal */}
      {showOrgSelection && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Select Organization</h3>
            <p style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>You belong to multiple organizations. Please select one to continue:</p>
            <div style={{ marginBottom: '1rem' }}>
              {organizations.map(org => (
                <label key={org.id} style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="organization" 
                    value={org.organizationId} 
                    checked={selectedOrgId === org.organizationId.toString()}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <strong>{org.organizationName}</strong> - {org.base} ({org.type})
                </label>
              ))}
            </div>
            {error && (
              <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={handleOrgSelection}
                style={{ backgroundColor: '#667eea', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Change Dialog */}
      {showPasswordChange && pendingUser && (
        <PasswordChangeDialog
          sessionId={pendingUser.sessionId}
          onComplete={handlePasswordChangeComplete}
          onCancel={handleDialogCancel}
        />
      )}
      
      {/* Legal Statement Dialog */}
      {showLegalStatement && pendingUser && (
        <StatementDialog
          type="legal"
          sessionId={pendingUser.sessionId}
          onAcknowledge={handleLegalStatementAcknowledge}
          onCancel={handleDialogCancel}
        />
      )}
      
      {/* Privacy Statement Dialog */}
      {showPrivacyStatement && pendingUser && (
        <StatementDialog
          type="privacy"
          sessionId={pendingUser.sessionId}
          onAcknowledge={handlePrivacyStatementAcknowledge}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Unsigned Agreement Dialog */}
      {currentAgreement && pendingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem 1.5rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, flex: 1, textAlign: 'center' }}>Please Review and Sign: {currentAgreement.name || `Agreement ${unsignedAgreements.indexOf(currentAgreement) + 1}`} ({unsignedAgreements.indexOf(currentAgreement) + 1} of {unsignedAgreements.length})</h3>
              {agreementPdfUrl && (
                <a href={agreementPdfUrl} download={`${currentAgreement.name || 'agreement'}.pdf`} style={{ padding: '0.4rem 0.9rem', backgroundColor: '#17a2b8', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>⬇ Download PDF</a>
              )}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {agreementPdfUrl ? (
                <iframe src={agreementPdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Agreement PDF" />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>Loading agreement...</div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              {agreementError && (
                <div style={{ padding: '0.5rem', marginBottom: '0.75rem', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>{agreementError}</div>
              )}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.9rem' }}>Full Name (signature) *</label>
                  <input type="text" value={agreementSignData.signedName} onChange={(e) => setAgreementSignData({...agreementSignData, signedName: e.target.value})} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '200px' }} placeholder="Your full name" />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.9rem' }}>Date of Birth *</label>
                  <input type="date" value={agreementSignData.dob} onChange={(e) => setAgreementSignData({...agreementSignData, dob: e.target.value})} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.9rem' }}>Parent/Guardian {(() => { const d = new Date(agreementSignData.dob); const today = new Date(); const age = agreementSignData.dob ? today.getFullYear() - d.getFullYear() - (today < new Date(today.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0) : 99; return age < 18 ? '(Required — under 18) *' : '(if minor)'; })()}</label>
                  <input type="text" value={agreementSignData.signedParent} onChange={(e) => setAgreementSignData({...agreementSignData, signedParent: e.target.value})} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px' }} placeholder="Optional" />
                </div>
                <div style={{ alignSelf: 'flex-end', maxWidth: '260px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: '#333' }}>
                    <input type="checkbox" checked={agreementRead} onChange={(e) => setAgreementRead(e.target.checked)} style={{ marginTop: '2px', flexShrink: 0 }} />
                    I have read and reviewed this agreement
                  </label>
                  <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.8rem', color: '#555', lineHeight: '1.4' }}>By entering your full name and clicking <strong>I Agree &amp; Sign</strong>, you acknowledge that you have read and agree to the terms of this agreement. This constitutes your legal electronic signature.</p>
                  <button onClick={handleSignAgreement} disabled={signingAgreement || !agreementRead} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: (signingAgreement || !agreementRead) ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: (signingAgreement || !agreementRead) ? 0.5 : 1, width: '100%' }}>
                    {signingAgreement ? 'Signing...' : 'I Agree & Sign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;